import {
  Calendar as AriaCalendar,
  CalendarGridHeader as AriaCalendarGridHeader,
  CalendarProps as AriaCalendarProps,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarHeaderCell,
  DateValue,
  Heading,
  Text,
  useLocale,
} from "react-aria-components";
import { focusRing } from "../../utils";
import { Button } from "../Button";
import { Icon } from "../Icon";

function getCellStyles({ isSelected, isDisabled }: { isSelected: boolean; isDisabled: boolean }) {
  const baseStyles = [
    focusRing,
    "w-9 h-9 text-sm cursor-default rounded-sm flex items-center justify-center forced-color-adjust-none font-semibold",
  ];

  if (isDisabled) {
    baseStyles.push("text-text-disabled forced-colors:text-[GrayText]");
  } else if (isSelected) {
    baseStyles.push(
      "bg-action-default text-text-onPrimary invalid:bg-feedback-error-border forced-colors:bg-[Highlight] forced-colors:invalid:bg-[Mark] forced-colors:text-[HighlightText]"
    );
  } else {
    baseStyles.push("text-text-body hover:bg-background-hover pressed:bg-background-selected");
  }

  return baseStyles.join(" ");
}

/**
 * Calendar
 *
 * Single-date calendar primitive with Edges styling.
 */
export interface CalendarProps<T extends DateValue> extends Omit<AriaCalendarProps<T>, "visibleDuration"> {
  errorMessage?: string;
}

export function Calendar<T extends DateValue>({ errorMessage, ...props }: CalendarProps<T>) {
  return (
    <AriaCalendar {...props}>
      <CalendarHeader />
      <CalendarGrid className="border-separate [border-spacing:0] [&_td]:p-0 [&_td]:!border-0">
        <CalendarGridHeader />
        <CalendarGridBody>
          {(date) => (
            <CalendarCell
              date={date}
              className={({ isSelected, isDisabled }) => `${getCellStyles({ isSelected, isDisabled })} border-0`}
            />
          )}
        </CalendarGridBody>
      </CalendarGrid>
      {errorMessage && (
        <Text slot="errorMessage" className="text-sm text-red-600">
          {errorMessage}
        </Text>
      )}
    </AriaCalendar>
  );
}

export function CalendarHeader() {
  const { direction } = useLocale();

  const buttonSize = 18;

  return (
    <header className="flex w-full items-center gap-1 px-1 pb-4">
      <Button variant="icon" slot="previous" className="pl-0">
        {direction === "rtl" ? (
          <Icon name="CaretRight" aria-hidden size={buttonSize} />
        ) : (
          <Icon name="CaretLeft" aria-hidden size={buttonSize} />
        )}
      </Button>
      <Heading className="text-md mx-2 flex-1 text-center font-semibold text-zinc-900 dark:text-zinc-200" />
      <Button variant="icon" slot="next" className="pr-0">
        {direction === "rtl" ? (
          <Icon name="CaretLeft" aria-hidden size={buttonSize} />
        ) : (
          <Icon name="CaretRight" aria-hidden size={buttonSize} />
        )}
      </Button>
    </header>
  );
}

export function CalendarGridHeader() {
  return (
    <AriaCalendarGridHeader>
      {(day) => <CalendarHeaderCell className="text-xs font-semibold text-text-caption">{day}</CalendarHeaderCell>}
    </AriaCalendarGridHeader>
  );
}
